import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { FormsModule, ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { goPasswordValidator } from '@go-modules/form-validator/go-password/go-password.validator';
import type { ResetPassword } from '@go-modules/services/auth/interfaces/auth-reset-password.interface';
import { VerificationModes } from '@go-modules/components/email-verification/email-verification.component';
import { NgxAuthService } from '@go-modules/services/auth/auth.service';
import { Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { EmailVerificationModule } from '@go-modules/components/email-verification/email-verification.module';
import { PasswordVisibilityToggleModule } from '@go-modules/directives/password-visibility-toggle/password-visibility-toggle.module';
import { FocusOnFirstInvalidFieldModule } from '@go-modules/directives/focus-on-first-invalid-field';
import { PasswordRequirementsModule } from '@go-modules/directives/password-requirements/password-requirements.module';
import { TranslateModule } from '@ngx-translate/core';
import { GoMaterialModule } from '@go-modules/go-material.module';
import { AuthTemplateModule } from '@go-modules/components/auth-template/auth-template.module';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ROUTE_NAMES, RouterService } from '@go-modules/services/router/router.service';

@Component({
	selector: 'reset-password',
	imports: [
		CommonModule,
		TranslateModule,
		GoMaterialModule,
		ReactiveFormsModule,
		FormsModule,
		MatTooltipModule,
		AuthTemplateModule,
		EmailVerificationModule,
		PasswordVisibilityToggleModule,
		FocusOnFirstInvalidFieldModule,
		PasswordRequirementsModule
	],
	templateUrl: './reset-password.component.html',
	styleUrls: ['./reset-password.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class ResetPasswordComponent implements OnInit {
	public form: UntypedFormGroup;
	public loading = false;
	public resetLinkExpired = false;
	public resetSuccess = false;
	public resetFailed = false;
	public emailSignature;
	public verificationModes = VerificationModes;
	public email: string;
	public expiry: number;
	public hash: string;

	constructor (
		public router: Router,
		private routerService: RouterService,
		private authService: NgxAuthService,
		private cdr: ChangeDetectorRef,
		private fb: UntypedFormBuilder
	) {
		this.form = this.fb.group({
			password: ['', [Validators.required, goPasswordValidator()]]
		});
	}

	public ngOnInit (): void {
		this.email = this.routerService.getCurrentParams().email;
		this.expiry = Number(this.routerService.getCurrentParams().expiry);
		this.hash = this.routerService.getCurrentParams().hash;

		const expiryDate = new Date(this.expiry * 1000);
		if (new Date() >= expiryDate) {
			this.resetLinkExpired = true;
		}
	}

	public navigateToForgotPage () {
		// video share
		if (this.routerService.getCurrentData().isVideoShare) {
			this.router.navigate(['/forgot'], {queryParamsHandling: 'preserve', replaceUrl: true});
		} else {
			this.routerService.navigateTo(ROUTE_NAMES.AUTH_FORGOT, {}, { onSameUrlNavigation: 'reload', replaceUrl: true });
		}
	}

	public navigateToLogin () {
		if (this.routerService.getCurrentData().isVideoShare) {
			this.router.navigate(['/login'], {queryParamsHandling: 'preserve', replaceUrl: true});
		} else {
			this.routerService.navigateTo(ROUTE_NAMES.AUTH_LOGIN, {}, { onSameUrlNavigation: 'reload', replaceUrl: true });
		}
	}

	public verifyEmail (res) {
		this.emailSignature = res.email_signature;
	}

	public submit (): void {
		if (this.form.valid) {
			this.loading = true;

			const requestData: ResetPassword = {
				password: this.form.controls.password.value,
				email: this.email,
				expiration: this.expiry,
				hash: this.hash,
				email_signature: this.emailSignature
			};

			this.authService.resetPassword(requestData).subscribe({
				next: () => {
					this.loading = false;
					this.resetSuccess = true;
					this.cdr.detectChanges();
				}, error: (_error) => {
					this.loading = false;
					this.resetFailed = true;
					this.cdr.detectChanges();
				}
			});
		}
	}
}
